<template>
  <div class="malaria-section">
    <div class="section-title" @click="toggleSection('summary')">
      <span>{{ $t('whole-slide-result') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.summary }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.summary" class="section-content">
      <div class="columns is-mobile is-multiline m-0">
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered malaria-status-box" :class="statusClass">
            <div class="has-text-grey">{{ $t('malaria-status') }}</div>
            <div class="is-size-5 has-text-weight-bold">{{ displayMalariaStatus(result.summary.status) }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('parasites') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-primary">{{ result.summary.parasiteCount }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('wbcs') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-info">{{ result.summary.wbcCount }}</div>
          </div>
        </div>
      </div>

      <div class="malaria-term-list">
        <div v-for="row in termRows" :key="row.type" class="malaria-term-card">
          <div class="malaria-term-header">
            <button class="algorithm-term-toggle" type="button" @click="toggleTermRow(row.type)">
              <span class="term-arrow">{{ isTermRowExpanded(row.type) ? '▼' : '▶' }}</span>
            </button>
            <label class="switch is-small malaria-term-annotation-switch">
              <input
                type="checkbox"
                :checked="row.overlayVisible"
                :data-testid="`malaria-overlay-toggle-${row.type}`"
                @change="updateOverlayVisibility(row.type, $event.target.checked)"
              >
              <span class="switch-slider"></span>
            </label>
            <div class="malaria-term-title">
              <span class="dot" :class="row.dotClass"></span>
              <span class="term-label-cap">{{ $t(row.labelKey) }}</span>
            </div>
          </div>

          <div class="malaria-term-summary">
            <div class="malaria-term-summary-row">
              <span>{{ $t('count') }}</span>
              <strong>{{ row.count }}</strong>
            </div>
            <div class="malaria-term-summary-row">
              <span>{{ $t('mean-confidence') }}</span>
              <strong>{{ formatPercent(row.meanConfidence) }}</strong>
            </div>
          </div>

          <div class="malaria-term-controls">
            <div class="malaria-term-threshold">
              <div class="malaria-threshold-label">
                <button
                  class="malaria-threshold-toggle"
                  type="button"
                  :data-testid="`malaria-threshold-toggle-${row.type}`"
                  @click="toggleThresholdControls(row.type)"
                >
                  <span class="term-arrow">{{ isThresholdControlsExpanded(row.type) ? '▼' : '▶' }}</span>
                  <span>{{ $t('confidence-threshold') }}</span>
                </button>
                <div class="malaria-threshold-actions">
                  <span class="malaria-control-label">{{ $t('confidence') }}</span>
                  <label class="switch is-small malaria-term-confidence-label-switch">
                    <input
                      type="checkbox"
                      :checked="row.confidenceLabelVisible"
                      :data-testid="`malaria-confidence-label-toggle-${row.type}`"
                      @change="updateConfidenceLabelVisibility(row.type, $event.target.checked)"
                    >
                    <span class="switch-slider"></span>
                  </label>
                  <span
                    class="malaria-threshold-value"
                    :data-testid="`malaria-threshold-value-${row.type}`"
                  >{{ formatDecimal(displayThreshold(row.type)) }}</span>
                </div>
              </div>
              <input
                v-if="isThresholdControlsExpanded(row.type)"
                class="slider is-fullwidth"
                :data-testid="`malaria-threshold-slider-${row.type}`"
                type="range"
                min="0"
                max="1"
                step="0.01"
                :value="displayThreshold(row.type)"
                @input="updateThresholdDraft(row.type, $event.target.value)"
                @change="commitThreshold(row.type, $event.target.value)"
              >
            </div>
          </div>

          <div v-if="isTermRowExpanded(row.type)" class="malaria-term-detections">
            <div v-if="paginatedDetectionsForType(row.type).length" class="malaria-detection-previews">
              <div
                v-for="detection in paginatedDetectionsForType(row.type)"
                :key="detection.id"
                class="malaria-detection-preview"
              >
                <annotation-preview
                  :annot="annotationForDetection(detection)"
                  :size="72"
                  :color="row.previewColor"
                  :terms="allTerms"
                  :images="images"
                  :show-image-info="false"
                  :same-view-on-click="true"
                  @updateTermsOrTracks="$emit('updateTermsOrTracks', annotationForDetection(detection))"
                  @centerView="$emit('centerView', { annot: annotationForDetection(detection), sameView: true })"
                  @deletion="$emit('deletion', annotationForDetection(detection))"
                  @select="$emit('select', $event)"
                />
                <div class="malaria-detection-confidence">{{ formatDetectionConfidence(detection) }}</div>
              </div>
            </div>
            <div v-else class="has-text-centered has-text-grey malaria-detection-empty">
              {{ $t('no-detections-current-threshold') }}
            </div>
            <cytomine-pagination
              v-if="detectionsForType(row.type).length > detectionPageSize"
              class="malaria-detection-pagination"
              size="is-small"
              order="is-centered"
              :total="detectionsForType(row.type).length"
              :current.sync="detectionPages[row.type]"
              :per-page="detectionPageSize"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('density')">
      <span>{{ $t('parasite-density') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.density }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.density" class="section-content">
      <div class="box p-3 scoring-details-box">
        <div class="has-text-grey has-text-weight-semibold mb-2">{{ $t('who-thick-film-formula') }}</div>
        <div class="malaria-formula mb-3">
          {{ $t('parasite-density-formula') }}
        </div>
        <table class="table is-fullwidth is-size-7 centered-table">
          <tbody>
            <tr>
              <td>{{ $t('parasites-counted-ai') }}</td>
              <td class="has-text-centered">{{ result.summary.parasiteCount }}</td>
            </tr>
            <tr>
              <td>{{ $t('wbcs-counted-ai') }}</td>
              <td class="has-text-centered">{{ result.summary.wbcCount }}</td>
            </tr>
            <tr>
              <td>{{ $t('assumed-patient-wbc-count') }}</td>
              <td class="has-text-centered">{{ formatInteger(result.density.wbcPerMicroliter) }}/µL</td>
            </tr>
            <tr>
              <td>{{ $t('estimated-parasite-density') }}</td>
              <td class="has-text-centered has-text-weight-bold has-text-success">
                {{ densityLabel }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('report')">
      <span>{{ $t('report') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.report }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.report" class="section-content report-form">
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('malaria-status') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.status">
                <option value="Detected">{{ $t('malaria-detected') }}</option>
                <option value="Not Detected">{{ $t('malaria-not-detected') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('species') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select data-testid="malaria-species-select" v-model="result.report.species">
                <option v-for="species in speciesOptions" :key="species" :value="species">{{ species }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center" data-testid="malaria-report-parasite-density">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('parasite-density') }}</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-control-fill">
            <input
              class="input is-small"
              data-testid="malaria-report-parasite-density-input"
              type="number"
              min="0"
              step="1"
              v-model.number="reportParasiteDensity"
            >
          </div>
          <span class="report-unit-label" data-testid="malaria-report-parasite-density-unit">parasites/µL</span>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('review-status') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.reviewStatus">
                <option value="Pending">{{ $t('review-status-pending') }}</option>
                <option value="Confirmed">{{ $t('review-status-confirmed') }}</option>
                <option value="Rejected">{{ $t('review-status-rejected') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <button class="button is-primary is-small is-fullwidth mt-3">{{ $t('confirm-results') }}</button>
    </div>
  </div>
</template>

<script>
import { Annotation, Cytomine } from '@/api';
import AnnotationPreview from '@/components/annotations/AnnotationPreview';
import CytominePagination from '@/components/utils/CytominePagination';
import { includedMalariaDetections, recomputeMalariaResult } from './ai-models/malaria';
import { visibilityByTerm } from './ai-models/term-visualization';

const MALARIA_TERM_TYPES = ['parasite', 'wbc'];
const MALARIA_TERM_CONFIG = {
  parasite: {
    labelKey: 'parasite',
    dotClass: 'parasite-dot',
    previewColor: '23d160'
  },
  wbc: {
    labelKey: 'wbc',
    dotClass: 'wbc-dot',
    previewColor: '3e8ed0'
  }
};

export default {
  name: 'malaria-result-panel',
  components: {
    AnnotationPreview,
    CytominePagination
  },
  props: {
    result: {
      type: Object,
      required: true
    },
    project: Object,
    index: String,
    allTerms: {
      type: Array,
      default: () => []
    },
    ontologyTerms: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Array,
      default: () => []
    },
    activeSliceId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      expandedTermRows: {
        parasite: false,
        wbc: false
      },
      expandedThresholdControls: {
        parasite: false,
        wbc: false
      },
      detectionPageSize: 50,
      detectionPages: {
        parasite: 1,
        wbc: 1
      },
      draftThresholdsByType: {},
      speciesOptions: [
        'Unknown / Not specified',
        'Plasmodium falciparum',
        'Plasmodium vivax',
        'Plasmodium malariae',
        'Plasmodium ovale',
        'Plasmodium knowlesi',
        'Mixed infection'
      ]
    };
  },
  computed: {
    statusClass() {
      return this.result.summary.status === 'Detected' ? 'is-detected' : 'is-not-detected';
    },
    densityLabel() {
      if (this.result.density.parasitesPerMicroliter === null) {
        return 'N/A';
      }
      return `${this.formatInteger(this.result.density.parasitesPerMicroliter)} parasites/µL`;
    },
    reportParasiteDensity: {
      get() {
        return this.result.report.parasitesPerMicroliter ?? this.result.density.parasitesPerMicroliter;
      },
      set(value) {
        this.$set(this.result.report, 'parasitesPerMicroliter', value);
      }
    },
    includedDetections() {
      return includedMalariaDetections(this.result);
    },
    termRows() {
      const rows = [this.termRow('parasite')];
      const hasWbc = this.result.isTrainingReport === true ||
        (this.result.derived?.totalByType?.wbc || 0) > 0 ||
        this.result.summary.wbcCount > 0;
      if (hasWbc) {
        rows.push(this.termRow('wbc'));
      }
      return rows;
    }
  },
  mounted() {
    this.ensureInitialVisualizationState();
    this.ensureReportDefaults();
  },
  methods: {
    toggleSection(sectionName) {
      if (this.result.sectionCollapsed && typeof this.result.sectionCollapsed[sectionName] !== 'undefined') {
        this.result.sectionCollapsed[sectionName] = !this.result.sectionCollapsed[sectionName];
      }
    },
    ensureReportDefaults() {
      if (!this.result.report.species || this.result.report.species === 'N/A') {
        this.result.report.species = 'Unknown / Not specified';
      }
      if (this.result.report.parasitesPerMicroliter === undefined || this.result.report.parasitesPerMicroliter === null) {
        this.$set(this.result.report, 'parasitesPerMicroliter', this.result.density.parasitesPerMicroliter);
      }
      this.syncReportFromSummary();
    },
    ensureInitialVisualizationState() {
      this.ensureThresholds();
      this.overlayVisibleByType();
      this.confidenceLabelsVisibleByType();
    },
    ensureThresholds() {
      const hasConfidenceThreshold = this.result.thresholds?.confidence !== undefined && this.result.thresholds?.confidence !== null;
      if (!this.result.thresholds) {
        this.$set(this.result, 'thresholds', { confidence: 0.4, byType: {} });
      }
      if (!this.result.thresholds.byType) {
        this.$set(this.result.thresholds, 'byType', {});
      }
      const fallback = this.clampThreshold(this.result.thresholds.confidence);
      if (this.result.thresholds.byType.parasite === undefined) {
        this.$set(this.result.thresholds.byType, 'parasite', hasConfidenceThreshold ? fallback : 0.65);
      }
      if (this.result.thresholds.byType.wbc === undefined) {
        this.$set(this.result.thresholds.byType, 'wbc', fallback);
      }
    },
    thresholdForType(type) {
      this.ensureThresholds();
      return this.clampThreshold(this.result.thresholds.byType[type]);
    },
    displayThreshold(type) {
      return this.draftThresholdsByType[type] ?? this.thresholdForType(type);
    },
    updateThresholdDraft(type, value) {
      this.$set(this.draftThresholdsByType, type, this.clampThreshold(value));
    },
    commitThreshold(type, value) {
      const threshold = this.draftThresholdsByType[type] ?? this.clampThreshold(value);
      this.$delete(this.draftThresholdsByType, type);
      this.updateThreshold(type, threshold);
    },
    updateThreshold(type, value) {
      this.ensureThresholds();
      this.$set(this.result.thresholds.byType, type, this.clampThreshold(value));
      this.resetDetectionPage(type);
      this.recomputeAndEmit({ includeVisualizations: false });
    },
    ensureTypedVisibility(sectionName, defaultVisible) {
      if (!this.result[sectionName]) {
        this.$set(this.result, sectionName, { visible: defaultVisible, byTypeVisible: {}, points: [] });
      }
      const section = this.result[sectionName];
      if (!section.byTypeVisible) {
        this.$set(section, 'byTypeVisible', {});
      }
      MALARIA_TERM_TYPES.forEach(type => {
        if (section.byTypeVisible[type] === undefined) {
          this.$set(section.byTypeVisible, type, section.visible !== false);
        }
      });
      return section.byTypeVisible;
    },
    overlayVisibleByType() {
      return this.ensureTypedVisibility('overlay', true);
    },
    isOverlayVisibleForType(type) {
      return this.overlayVisibleByType()[type] === true;
    },
    confidenceLabelsVisibleByType() {
      if (!this.result.overlay) {
        this.$set(this.result, 'overlay', { visible: true, byTypeVisible: {}, points: [] });
      }
      if (!this.result.overlay.showConfidenceLabelsByType) {
        this.$set(this.result.overlay, 'showConfidenceLabelsByType', {});
      }
      MALARIA_TERM_TYPES.forEach(type => {
        if (this.result.overlay.showConfidenceLabelsByType[type] === undefined) {
          this.$set(this.result.overlay.showConfidenceLabelsByType, type, false);
        }
      });
      return visibilityByTerm(MALARIA_TERM_TYPES, this.result.overlay, false, 'showConfidenceLabelsByType');
    },
    isConfidenceLabelVisibleForType(type) {
      return this.confidenceLabelsVisibleByType()[type] !== false;
    },
    updateConfidenceLabelVisibility(type, visible) {
      this.confidenceLabelsVisibleByType();
      this.$set(this.result.overlay.showConfidenceLabelsByType, type, visible);
      this.emitVisualization();
    },
    updateOverlayVisibility(type, visible) {
      this.overlayVisibleByType();
      this.$set(this.result.overlay.byTypeVisible, type, visible);
      this.result.overlay.visible = Object.values(this.result.overlay.byTypeVisible).some(Boolean);
      this.emitVisualization();
    },
    clampThreshold(value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return 0.4;
      }
      return Math.max(0, Math.min(1, numberValue));
    },
    toggleTermRow(type) {
      this.$set(this.expandedTermRows, type, !this.expandedTermRows[type]);
      this.ensureDetectionPage(type);
    },
    isTermRowExpanded(type) {
      return this.expandedTermRows[type] === true;
    },
    toggleThresholdControls(type) {
      this.$set(this.expandedThresholdControls, type, !this.expandedThresholdControls[type]);
    },
    isThresholdControlsExpanded(type) {
      return this.expandedThresholdControls[type] === true;
    },
    recomputeAndEmit(options = {}) {
      Object.assign(this.result, recomputeMalariaResult(this.result, options));
      this.syncReportFromSummary();
      this.emitVisualization();
    },
    emitVisualization() {
      this.emitSettings();
      this.emitOverlay();
    },
    emitSettings() {
      this.$emit('settingsChange', {
        showAllAnnotations: this.result.showAllAnnotations,
        thresholds: {
          confidence: this.result.thresholds?.confidence,
          byType: {
            ...(this.result.thresholds?.byType || {})
          }
        },
        overlay: {
          visible: this.result.overlay?.visible,
          opacity: this.result.overlay?.opacity,
          byTypeVisible: {
            ...(this.result.overlay?.byTypeVisible || {})
          },
          showConfidenceLabelsByType: {
            ...(this.result.overlay?.showConfidenceLabelsByType || {})
          }
        }
      });
    },
    emitOverlay() {
      this.$emit('overlayChange', {
        visible: this.result.showAllAnnotations && this.result.overlay.visible,
        opacity: this.result.overlay.opacity,
        thresholds: {
          confidence: this.result.thresholds?.confidence,
          byType: {
            ...(this.result.thresholds?.byType || {})
          }
        },
        byTypeVisible: {
          ...(this.result.overlay.byTypeVisible || {})
        },
        showConfidenceLabelsByType: {
          ...(this.result.overlay.showConfidenceLabelsByType || {})
        },
        zIndex: 3
      });
    },
    updateWbcCount() {
      this.result.density.wbcPerMicroliter = Number(this.result.report.wbcPerMicroliter) || 8000;
      this.recomputeAndEmit();
    },
    syncReportFromSummary() {
      if (this.result.summary?.status) {
        this.$set(this.result.report, 'status', this.result.summary.status);
      }
      if (this.result.summary?.species) {
        this.$set(this.result.report, 'species', this.result.summary.species);
      }
      if (this.result.summary?.reviewStatus) {
        this.$set(this.result.report, 'reviewStatus', this.result.summary.reviewStatus);
      }
      if (this.result.density?.parasitesPerMicroliter !== undefined && this.result.density?.parasitesPerMicroliter !== null) {
        this.$set(this.result.report, 'parasitesPerMicroliter', this.result.density.parasitesPerMicroliter);
      }
    },
    termRow(type) {
      const config = MALARIA_TERM_CONFIG[type];
      return {
        type,
        labelKey: config.labelKey,
        dotClass: config.dotClass,
        previewColor: config.previewColor,
        overlayVisible: this.isOverlayVisibleForType(type),
        confidenceLabelVisible: this.isConfidenceLabelVisibleForType(type),
        count: type === 'parasite' ? this.result.summary.parasiteCount : this.result.summary.wbcCount,
        meanConfidence: this.meanConfidence(type),
        threshold: this.thresholdForType(type)
      };
    },
    displayMalariaStatus(status) {
      if (status === 'Detected') {
        return this.$t('malaria-detected');
      }
      if (status === 'Not Detected') {
        return this.$t('malaria-not-detected');
      }
      return status;
    },
    detectionsForType(type) {
      const derivedDetections = this.result.derived?.sortedIncludedByType?.[type];
      if (Array.isArray(derivedDetections)) {
        return derivedDetections;
      }
      return this.includedDetections
        .filter(detection => this.normalizedDetectionType(detection) === type)
        .slice()
        .sort((a, b) => b.confidence - a.confidence);
    },
    ensureDetectionPage(type) {
      if (!this.detectionPages[type]) {
        this.$set(this.detectionPages, type, 1);
      }
    },
    resetDetectionPage(type) {
      this.$set(this.detectionPages, type, 1);
    },
    paginatedDetectionsForType(type) {
      this.ensureDetectionPage(type);
      const detections = this.detectionsForType(type);
      const maxPage = Math.max(1, Math.ceil(detections.length / this.detectionPageSize));
      const currentPage = Math.min(this.detectionPages[type], maxPage);
      if (currentPage !== this.detectionPages[type]) {
        this.$set(this.detectionPages, type, currentPage);
      }
      const start = (currentPage - 1) * this.detectionPageSize;
      return detections.slice(start, start + this.detectionPageSize);
    },
    cytomineBasePath() {
      try {
        return `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      } catch (error) {
        return '/api/';
      }
    },
    annotationForDetection(detection) {
      const centroid = detection.centroid || {
        x: Number(detection.x),
        y: Number(detection.y)
      };
      const annot = new Annotation({
        id: detection.id,
        class: detection.class,
        created: detection.created,
        updated: detection.updated,
        location: detection.location || `POINT (${Number(centroid.x)} ${Number(centroid.y)})`,
        geometryCompression: detection.geometryCompression,
        centroid,
        user: detection.user,
        term: detection.term,
        userByTerm: detection.userByTerm,
        area: detection.area,
        areaUnit: detection.areaUnit,
        perimeter: detection.perimeter,
        perimeterUnit: detection.perimeterUnit,
        image: detection.image || this.images[0]?.id,
        project: detection.project || this.project?.id,
        slice: detection.slice || this.activeSliceId
      });
      const basePath = this.cytomineBasePath();
      annot.cropURL = detection.cropURL || `${basePath}annotation/${detection.id}/crop.png`;
      annot.smallCropURL = detection.smallCropURL || `${basePath}annotation/${detection.id}/crop.png?maxSize=256`;
      annot.malariaDetectionType = detection.type;
      annot.confidence = detection.confidence;
      annot.confidenceAvailable = detection.confidenceAvailable;
      annot.aiVisualizationType = detection.aiVisualizationType;
      return annot;
    },
    meanConfidence(type) {
      const derivedMean = this.result.derived?.meanConfidenceByType?.[type];
      if (derivedMean !== undefined) {
        return derivedMean;
      }
      const detections = this.detectionsForType(type);
      if (!detections.length) {
        return null;
      }
      return detections.reduce((sum, detection) => sum + detection.confidence, 0) / detections.length;
    },
    normalizedDetectionType(detection) {
      return this.isWbcDetection(detection) ? 'wbc' : 'parasite';
    },
    isWbcDetection(detection) {
      return ['wbc', 'white_blood_cell', 'white blood cell'].includes(String(detection.type || '').toLowerCase());
    },
    centerDetection(detection) {
      this.$emit('centerView', {
        sameView: true,
        annot: this.annotationForDetection(detection)
      });
    },
    formatPercent(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
      }
      return `${Math.round(Number(value) * 100)}%`;
    },
    formatDecimal(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDetectionConfidence(detection) {
      return detection.confidenceAvailable === false ? 'N/A' : this.formatDecimal(detection.confidence);
    },
    formatInteger(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
      }
      return Math.round(Number(value)).toLocaleString();
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/colors.scss';

.section-title {
  padding: 0.72rem 0.85rem;
  font-weight: 700;
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(222, 238, 255, 0.045);
  color: $dark-text-primary;
  align-items: center;
  margin-top: 8px;
  transition: background-color 0.16s ease;
}

.section-title:hover {
  background-color: rgba($primary, 0.08);
}

.section-title:first-child {
  margin-top: 0;
}

.section-content {
  padding: 0.8rem 0.85rem;
  background-color: rgba(8, 17, 29, 0.18);
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  font-size: 13px;
}

.section-content .table {
  font-size: 13px;
}

.section-content .label {
  font-size: 13px !important;
  font-weight: 500;
}

.section-content .input,
.section-content .select select {
  font-size: 13px !important;
  height: 2.25em;
}

.centered-table {
  background-color: transparent !important;
  color: $dark-text-primary !important;
}

.centered-table th {
  color: $dark-text-secondary !important;
  border-color: $dark-border-color !important;
  text-align: center !important;
}

.centered-table td {
  border-color: $dark-border-color !important;
  vertical-align: middle !important;
}

.centered-table tbody tr:hover {
  background-color: rgba($primary, 0.055);
}

.centered-table th:first-child,
.centered-table td:first-child {
  text-align: left !important;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.scoring-box,
.scoring-details-box,
.malaria-status-box {
  background: rgba(222, 238, 255, 0.038);
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked+.switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked+.switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked+.switch-slider:before {
  transform: translateX(16px);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  min-width: 12px;
  min-height: 12px;
}

.report-form .field-label {
  text-align: left;
  flex: 0 0 110px;
  max-width: 110px;
  padding-top: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.report-form .field-body {
  flex: 1 1 auto;
  min-width: 0;
  align-items: center !important;
  display: flex !important;
}

.report-form .control {
  display: flex !important;
  align-items: center !important;
  gap: 5px;
}

.report-form .field {
  margin-bottom: 0.65rem !important;
}

.report-form .field-label.report-label-wide {
  flex: 0 0 160px;
  max-width: 160px;
}

.report-form .control.report-control-fill {
  width: 100%;
}

.align-items-center {
  align-items: center;
}

.d-flex-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.malaria-status-box.is-detected {
  border-color: rgba($danger, 0.48);
  background: rgba($danger, 0.08);
}

.malaria-status-box.is-detected .is-size-5 {
  color: #ff7896;
}

.malaria-status-box.is-not-detected {
  border-color: rgba($success, 0.42);
  background: rgba($success, 0.07);
}

.malaria-status-box.is-not-detected .is-size-5 {
  color: #67e09a;
}

.malaria-formula {
  font-size: 0.82rem;
  color: $dark-text-primary;
  padding: 0.45rem 0.55rem;
  border: 1px solid $dark-border-color;
  border-radius: 7px;
  background: rgba(222, 238, 255, 0.035);
}

.malaria-detection-table {
  margin-top: 0.5rem;
}

.malaria-term-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin: 0.85rem 0;
}

.malaria-visualization-block {
  margin-top: 0.85rem;
}

.malaria-term-card {
  background: rgba(222, 238, 255, 0.035);
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
  padding: 0.75rem;
}

.malaria-term-header {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  min-width: 0;
}

.malaria-term-title {
  align-items: center;
  color: $dark-text-primary;
  display: flex;
  flex: 1 1 auto;
  font-weight: 600;
  min-width: 0;
}

.malaria-term-summary {
  border-top: 1px solid rgba(190, 214, 238, 0.1);
  margin-top: 0.55rem;
  padding-top: 0.45rem;
}

.malaria-term-summary-row {
  align-items: center;
  color: $dark-text-secondary;
  display: flex;
  font-size: 0.82rem;
  justify-content: space-between;
  line-height: 1.7;
}

.malaria-term-summary-row strong {
  color: $dark-text-primary;
  font-weight: 600;
}

.malaria-term-controls {
  align-items: center;
  display: flex;
  margin-top: 0.65rem;
}

.malaria-term-threshold {
  flex: 1 1 auto;
  min-width: 0;
}

.malaria-threshold-label {
  align-items: center;
  color: $dark-text-secondary;
  display: flex;
  font-size: 0.78rem;
  justify-content: space-between;
  margin-bottom: 0.15rem;
}

.malaria-threshold-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: $dark-text-secondary;
  cursor: pointer;
  display: flex;
  font-size: 0.78rem;
  font-weight: 600;
  gap: 0.25rem;
  min-width: 0;
  padding: 0;
}

.malaria-threshold-toggle:hover,
.malaria-threshold-toggle:focus {
  color: $dark-text-primary;
}

.malaria-threshold-actions {
  align-items: center;
  display: flex;
  gap: 0.45rem;
}

.malaria-control-label {
  color: $dark-text-secondary;
  font-size: 0.75rem;
  line-height: 1;
}

.malaria-threshold-value {
  background: rgba(222, 238, 255, 0.06);
  border: 1px solid rgba(190, 214, 238, 0.13);
  border-radius: 6px;
  color: $dark-text-primary;
  font-weight: 600;
  line-height: 1;
  min-width: 3rem;
  padding: 0.22rem 0.35rem;
  text-align: center;
}

.malaria-term-detections {
  margin-top: 0.6rem;
}

.malaria-detection-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.55rem;
  padding-top: 0.2rem;
}

.malaria-detection-preview {
  align-items: center;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.malaria-detection-preview :deep(.annot-preview) {
  margin: 0;
}

.malaria-detection-confidence {
  color: $dark-text-primary;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  margin-top: 0.35rem;
}

.malaria-detection-empty {
  font-size: 0.8rem;
  padding: 0.65rem 0;
}

.malaria-detection-pagination {
  margin-top: 0.75rem;
}

.malaria-detection-pagination :deep(.pagination-list) {
  justify-content: center;
}

.term-label-cap {
  text-transform: capitalize;
}

.algorithm-term-toggle {
  background: transparent;
  border: none;
  color: $dark-text-secondary;
  cursor: pointer;
  margin-right: 0.25rem;
  padding: 0;
}

.algorithm-term-toggle:hover {
  color: $dark-text-primary;
}

.term-arrow {
  display: inline-block;
  min-width: 1rem;
  text-align: center;
}

.malaria-term-annotation-switch {
  flex: 0 0 auto;
  margin-right: 0.25rem;
}

.malaria-detection-row {
  cursor: pointer;
}

.malaria-detection-row:hover {
  background: rgba($primary, 0.07);
}

.parasite-dot {
  background: $success;
}

.wbc-dot {
  background: $info;
}
</style>
